/**
 * OPS-DASH-002 — Compact live operational summary strip.
 * Quick glance only; all detailed analytics live in
 * Reports → Productivity Analytics (Live Operations tab).
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { subscribe } from '../lib/operationalSummaryStore';
import { TrendingUp, Timer, ArrowRight, AlertTriangle } from 'lucide-react';

// eslint-disable-next-line no-restricted-syntax -- DATE-DISPLAY-001: intentional browser-local live clock time
const fmtTime = (iso) => iso ? new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '—';

const STATUS_CHIP = {
  done: { label: 'Target Met', cls: 'bg-emerald-100 text-emerald-700' },
  on_track: { label: 'On Track', cls: 'bg-blue-100 text-blue-700' },
  behind: { label: 'Behind', cls: 'bg-rose-100 text-rose-700' },
  no_target: { label: 'No Target', cls: 'bg-slate-100 text-slate-500' },
};

const QUEUE_CHIPS = [
  { key: 'clarification', label: 'Clarif', status: 'clarification', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  { key: 'correction', label: 'Corr', status: 'pending_correction', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
  { key: 'qc', label: 'QC', status: 'qc_review', cls: 'bg-violet-50 text-violet-700 border-violet-200' },
  { key: 'mailaway', label: 'Mail', status: 'mail_away', cls: 'bg-cyan-50 text-cyan-700 border-cyan-200' },
];

const Stat = ({ label, value, testId }) => (
  <div className="flex items-baseline gap-1.5" data-testid={testId}>
    <span className="text-base font-bold text-slate-900 leading-none">{value}</span>
    <span className="text-[10px] text-slate-500 uppercase tracking-wide">{label}</span>
  </div>
);

const MiniProgress = ({ percent, status }) => {
  const pct = Math.min(percent ?? 0, 100);
  const color = status === 'done' ? 'bg-emerald-500' : status === 'behind' ? 'bg-rose-500' : 'bg-blue-500';
  return (
    <div className="w-20 h-1.5 bg-slate-100 rounded-full overflow-hidden">
      <div className={`h-full ${color} transition-all duration-500`} style={{ width: `${pct}%` }} />
    </div>
  );
};

export default function OperationalDashboard({ userId, onFilterStatus }) {
  const [data, setData] = useState(null);

  // COMPLETION-PERF-A1 S4: shared 60 s poller (hidden-tab pause, visible → immediate refresh).
  useEffect(() => subscribe((s) => { if (s.data) setData(s.data); }), []);

  if (!data) return null;
  const isLeader = data.role_view === 'leader';
  // MLS-DASHBOARD-ASSIGNMENT-UI-001: the "Team Today" analytics strip is removed from Team Leader dashboards
  // (Done/Remaining/SLA/Active + shortcut badges). Processors keep "My Day"; KPI summary + controls are untouched.
  if (isLeader) return null;
  const queues = isLeader ? (data.team?.queues || {}) : (data.queues || {});
  const go = (s) => onFilterStatus ? () => onFilterStatus(s) : undefined;

  return (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 flex flex-wrap items-center gap-x-5 gap-y-2"
      data-testid="operational-dashboard">
      <div className="flex items-center gap-2 shrink-0">
        <TrendingUp className="w-4 h-4 text-blue-600" />
        <span className="text-sm font-semibold text-slate-800">{isLeader ? 'Team Today' : 'My Day'}</span>
      </div>

      {isLeader ? <LeaderStats data={data} /> : <ProcessorStats data={data} />}

      <div className="flex items-center gap-1.5" data-testid="opdash-queue-chips">
        {QUEUE_CHIPS.map(q => (
          <button key={q.key} type="button" onClick={go(q.status)} disabled={!onFilterStatus}
            data-testid={`opdash-chip-${q.key}`}
            className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-md border ${q.cls} ${onFilterStatus ? 'hover:opacity-75 cursor-pointer' : 'cursor-default'}`}>
            {q.label} {queues[q.key] ?? 0}
          </button>
        ))}
      </div>

      <Link to="/analytics/productivity?tab=live" data-testid="opdash-analytics-link"
        className="ml-auto flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-800 whitespace-nowrap">
        View Detailed Analytics <ArrowRight className="w-3.5 h-3.5" />
      </Link>
    </div>
  );
}

const ProcessorStats = ({ data }) => {
  const t = data.target || {};
  const eta = data.estimated_finish || {};
  const chip = STATUS_CHIP[t.status] || STATUS_CHIP.on_track;
  return (
    <>
      <div className="flex items-center gap-2" data-testid="opdash-completed-summary">
        <Stat label={`of ${t.value ?? '—'} done`} value={data.completed_today} testId="opdash-stat-completed" />
        <MiniProgress percent={t.percent} status={t.status} />
        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${chip.cls}`}>{chip.label}</span>
      </div>
      <Stat label="remaining" value={data.remaining_queue} testId="opdash-stat-remaining" />
      <div className="flex items-center gap-1 text-xs text-slate-600" data-testid="opdash-stat-eta">
        <Timer className="w-3.5 h-3.5 text-slate-400" />
        {eta.status === 'target_met' ? 'Target met' : eta.status === 'projected' ? `Finish ≈ ${fmtTime(eta.eta)}` : eta.status === 'no_target' ? 'No target set' : 'ETA —'}
      </div>
    </>
  );
};

const LeaderStats = ({ data }) => {
  const team = data.team || {};
  const tgt = team.target || {};
  const sla = team.sla || {};
  const activeMembers = (data.members || []).filter(m => m.presence === 'active').length;
  const status = !tgt.value ? 'no_target' : tgt.percent >= 100 ? 'done' : tgt.percent >= 60 ? 'on_track' : 'behind';
  return (
    <>
      <div className="flex items-center gap-2" data-testid="opdash-team-summary">
        <Stat label={`of ${tgt.value ?? '—'} done`} value={team.completed_today ?? 0} testId="opdash-stat-team-completed" />
        <MiniProgress percent={tgt.percent} status={status} />
      </div>
      <Stat label="remaining" value={team.remaining_queue ?? 0} testId="opdash-stat-team-remaining" />
      <div className={`flex items-center gap-1 text-xs ${(sla.overdue ?? 0) > 0 ? 'text-rose-600' : 'text-slate-600'}`}
        data-testid="opdash-stat-sla">
        <AlertTriangle className="w-3.5 h-3.5" />
        SLA {sla.health_percent ?? 100}%{(sla.overdue ?? 0) > 0 && ` · ${sla.overdue} overdue`}
      </div>
      <Stat label="active" value={`${activeMembers}/${(data.members || []).length}`} testId="opdash-stat-active" />
    </>
  );
};
